import { Link } from "@tanstack/react-router";
import { HomeIcon, EatIcon, MarkerIcon, BasketIcon, FlameIcon } from "@/components/icons/NavIcons";

const tabs = [
  { to: "/", label: "Home", icon: HomeIcon, exact: true },
  { to: "/menu", label: "Menu", icon: EatIcon, exact: false },
  { to: "/locations", label: "Spots", icon: MarkerIcon, exact: false },
] as const;

export function MobileTabBar() {
  return (
    <>
      <div className="md:hidden h-16" aria-hidden />

      <nav
        aria-label="Primary"
        className="md:hidden fixed bottom-2 left-1/2 -translate-x-1/2 z-50 pb-[env(safe-area-inset-bottom)]"
      >
        <div className="group/bar relative flex items-center gap-0.5 rounded-full border border-cream/20 bg-gradient-to-b from-[#2a2520]/95 via-charcoal/90 to-[#16120e]/95 backdrop-blur-2xl shadow-[0_22px_50px_-12px_rgba(0,0,0,0.95),inset_0_1px_0_0_rgba(255,255,255,0.18),inset_0_-1px_0_0_rgba(0,0,0,0.55),inset_0_0_0_1px_rgba(255,255,255,0.04)] px-1.5 py-1.5 overflow-hidden">
          {/* glossy top sheen */}
          <span className="pointer-events-none absolute inset-x-0 top-0 h-1/2 rounded-t-full bg-gradient-to-b from-cream/[0.16] via-cream/[0.05] to-transparent" />
          {/* bottom depth shadow */}
          <span className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 rounded-b-full bg-gradient-to-t from-black/50 to-transparent" />
          {/* specular hairline */}
          <span className="pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-cream/60 to-transparent" />
          {/* warm ambient glow from the center flame */}
          <span className="pointer-events-none absolute -inset-px rounded-full bg-[radial-gradient(60%_140%_at_50%_100%,color-mix(in_oklab,var(--chili)_22%,transparent),transparent_60%)]" />
          {/* moving shine sweep */}
          <span aria-hidden className="pointer-events-none absolute inset-0 rounded-full overflow-hidden">
            <span className="absolute top-0 -left-1/3 h-full w-1/3 bg-gradient-to-r from-transparent via-cream/20 to-transparent skew-x-[-20deg] translate-x-[-100%] group-hover/bar:translate-x-[400%] transition-transform duration-[1400ms] ease-out" />
          </span>

          {tabs.slice(0, 2).map((t) => (
            <TabLink key={t.to} {...t} />
          ))}

          <Link
            to="/franchise"
            aria-label="Franchise"
            className="group relative mx-1 flex items-center justify-center min-h-11 min-w-11 size-11 rounded-full bg-gradient-to-br from-yellow via-orange-500 to-chili text-charcoal ring-2 ring-charcoal active:scale-95 transition-transform overflow-hidden shadow-[0_0_18px_-2px_color-mix(in_oklab,var(--chili)_80%,transparent),0_0_36px_-8px_color-mix(in_oklab,var(--yellow)_70%,transparent)] animate-[fab-pulse_2.2s_ease-in-out_infinite]"
          >
            <span aria-hidden className="pointer-events-none absolute inset-0 rounded-full bg-[radial-gradient(circle_at_50%_75%,rgba(255,220,120,0.9),rgba(255,120,40,0.55)_45%,transparent_70%)] animate-[fab-flicker_1.4s_ease-in-out_infinite]" />
            <span aria-hidden className="pointer-events-none absolute inset-x-2 bottom-1 h-3 rounded-full bg-yellow/70 blur-[6px] animate-[fab-ember_1.8s_ease-in-out_infinite]" />
            <FlameIcon size={18} className="relative drop-shadow-[0_0_4px_rgba(255,240,160,0.9)]" />
          </Link>

          {tabs.slice(2).map((t) => (
            <TabLink key={t.to} {...t} />
          ))}
        </div>
      </nav>
    </>
  );
}

function TabLink({
  to,
  label,
  icon: Icon,
  exact,
}: {
  to: string;
  label: string;
  icon: React.ComponentType<{ size?: number }>;
  exact: boolean;
}) {
  return (
    <Link
      to={to}
      activeOptions={{ exact }}
      aria-label={label}
      className="group relative flex items-center justify-center min-h-11 min-w-11 size-11 rounded-full text-cream/80 hover:text-cream transition-colors"
      activeProps={{ className: "text-yellow bg-yellow/15" }}
    >
      <Icon size={18} />
    </Link>
  );
}
